import { useState, useEffect } from 'react'
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Settings, Loader2, Square, Sparkles, X, AlertCircle } from 'lucide-react'
import { projectsApi } from '@/api/projects'
import { extractionApi } from '@/api/extraction'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { usePlanLimits } from '@/hooks/usePlanLimits'
import { ProcessingStatusModal } from '@/components/extraction/ProcessingStatusModal'

// Lazy-loaded sub-components
import DocumentUpload from './DocumentUpload'
import FieldManager from './FieldManager'
import ExtractionResults from './ExtractionResults'
import ExtractionRuns from './ExtractionRuns'
import ChatAssistant from './ChatAssistant'
import AnnotationsList from './AnnotationsList'

export default function ProjectDetail() {
  const { projectId } = useParams<{ projectId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState('documents')
  const [filterDocumentId, setFilterDocumentId] = useState<string | null>(null)
  const [showSettings, setShowSettings] = useState(false)
  const [showProcessingModal, setShowProcessingModal] = useState(false)
  const [activeJobId, setActiveJobId] = useState<string | null>(null)
  const { canChat } = usePlanLimits()
  const queryClient = useQueryClient()

  // One-shot banner for template-apply errors from the NewProject flow
  const [templateApplyError, setTemplateApplyError] = useState<string | null>(
    (location.state as { templateApplyError?: string } | null)?.templateApplyError || null
  )
  useEffect(() => {
    // Clear router state after first render so refresh/back-nav doesn't replay it
    if ((location.state as { templateApplyError?: string } | null)?.templateApplyError) {
      navigate(location.pathname, { replace: true, state: {} })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const stopJobMutation = useMutation({
    mutationFn: (jobId: string) => extractionApi.stopJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['extraction-jobs', projectId] })
      queryClient.invalidateQueries({ queryKey: ['documents', projectId] })
      queryClient.invalidateQueries({ queryKey: ['results', projectId] })
    },
  })

  const { data: project, isLoading } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId!),
    enabled: !!projectId,
  })

  // Check for active running jobs
  const { data: jobs = [] } = useQuery({
    queryKey: ['extraction-jobs', projectId],
    queryFn: () => extractionApi.listJobs(projectId!),
    enabled: !!projectId,
    refetchInterval: 5000,
  })
  const activeJob = jobs.find((j) => j.status === 'running' || j.status === 'queued')

  // Check for active summary generation
  const { data: summaryProgress } = useQuery({
    queryKey: ['summary-progress', projectId],
    queryFn: () => extractionApi.getSummaryProgress(projectId!),
    enabled: !!projectId,
    refetchInterval: 3000,
  })
  const summaryRunning = summaryProgress?.status === 'running'

  if (isLoading) {
    return (
      <div className="p-6 text-center text-muted-foreground">Loading project...</div>
    )
  }

  if (!project) {
    return (
      <div className="p-6 text-center">
        <p className="text-muted-foreground mb-4">Project not found</p>
        <Button asChild variant="outline"><Link to="/projects">Back to Projects</Link></Button>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button asChild variant="ghost" size="icon">
              <Link to="/projects"><ArrowLeft className="h-4 w-4" /></Link>
            </Button>
            <div>
              <h1 className="text-xl font-bold">{project.name}</h1>
              <p className="text-sm text-muted-foreground">
                {project.document_count} documents · {project.field_count} fields
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Active job indicator */}
            {activeJob && !showProcessingModal && (
              <div className="flex items-center gap-1">
                <button
                  onClick={() => {
                    setActiveJobId(activeJob.id)
                    setShowProcessingModal(true)
                  }}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-200 text-blue-700 text-xs font-medium hover:bg-blue-100 transition-colors"
                >
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Extracting {activeJob.processed_documents}/{activeJob.total_documents}
                </button>
                <button
                  onClick={() => {
                    if (confirm('Stop this extraction job? Documents already processed will keep their results.')) {
                      stopJobMutation.mutate(activeJob.id)
                    }
                  }}
                  className="p-1 rounded-full bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 transition-colors"
                  title="Stop extraction"
                >
                  <Square className="h-3 w-3" />
                </button>
              </div>
            )}
            {/* Summary generation indicator */}
            {summaryRunning && (
              <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-xs font-medium">
                <Sparkles className="h-3 w-3 animate-pulse" />
                Summarizing {summaryProgress?.done}/{summaryProgress?.total}
              </div>
            )}
            <Button onClick={() => setShowSettings(true)}>
              <Settings className="h-4 w-4 mr-1.5" />
              Configure Fields
            </Button>
          </div>
        </div>
      </div>

      {/* Template-apply error banner (one-shot, from NewProject flow) */}
      {templateApplyError && (
        <div className="border-b bg-amber-50 border-amber-200 px-6 py-3">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
            <div className="flex-1 text-sm text-amber-900">
              <div className="font-medium">Template could not be applied</div>
              <div className="text-amber-800 mt-0.5">{templateApplyError}</div>
            </div>
            <button
              type="button"
              onClick={() => setTemplateApplyError(null)}
              className="p-1 rounded hover:bg-amber-100 text-amber-700"
              aria-label="Dismiss"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex-1 overflow-hidden">
        <Tabs value={activeTab} onValueChange={(tab) => { setActiveTab(tab); if (tab !== 'results') setFilterDocumentId(null) }} className="flex flex-col h-full">
          <div className="border-b px-6">
            <TabsList className="h-auto bg-transparent rounded-none p-0 gap-6">
              <TabsTrigger
                value="documents"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent pb-3 pt-2"
              >
                Documents
              </TabsTrigger>
              <TabsTrigger
                value="runs"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent pb-3 pt-2"
              >
                Runs
              </TabsTrigger>
              <TabsTrigger
                value="results"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent pb-3 pt-2"
              >
                Results
              </TabsTrigger>
              <TabsTrigger
                value="annotations"
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent pb-3 pt-2"
              >
                Annotations
              </TabsTrigger>
              {canChat && (
                <TabsTrigger
                  value="chat"
                  className="rounded-none border-b-2 border-transparent data-[state=active]:border-primary data-[state=active]:bg-transparent pb-3 pt-2"
                >
                  Chat
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          <div className="flex-1 overflow-hidden">
            <TabsContent value="documents" className="h-full m-0 overflow-y-auto p-6">
              <DocumentUpload
                projectId={projectId!}
                onViewResults={(docId) => {
                  setFilterDocumentId(docId)
                  setActiveTab('results')
                }}
              />
            </TabsContent>
            <TabsContent value="runs" className="h-full m-0 overflow-y-auto p-6">
              <ExtractionRuns
                projectId={projectId!}
                onViewResults={(jobId) => {
                  setActiveTab('results')
                }}
              />
            </TabsContent>
            <TabsContent value="results" className="h-full m-0 overflow-y-auto p-6">
              <ExtractionResults
                projectId={projectId!}
                filterDocumentId={filterDocumentId}
                onClearFilter={() => setFilterDocumentId(null)}
              />
            </TabsContent>
            <TabsContent value="annotations" className="h-full m-0 overflow-y-auto p-6">
              <AnnotationsList projectId={projectId!} />
            </TabsContent>
            {canChat && (
              <TabsContent value="chat" className="h-full m-0 overflow-hidden">
                <ChatAssistant projectId={projectId!} />
              </TabsContent>
            )}
          </div>
        </Tabs>
      </div>

      {/* Settings Dialog */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Configure Extraction Fields</DialogTitle>
          </DialogHeader>
          <FieldManager projectId={projectId!} />
        </DialogContent>
      </Dialog>

      {/* Re-openable Processing Status Modal for active job */}
      {showProcessingModal && activeJob && (
        <ProcessingStatusModal
          projectId={projectId!}
          jobId={activeJob.id}
          onClose={() => setShowProcessingModal(false)}
        />
      )}
    </div>
  )
}
